import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// The back office's words for the Providers page (ADR-008). `ops-copy.ts` puts them together
// with the rest into the one catalogue the screens read.

export type ProviderCatalog = typeof EN;

const EN = {
  // Providers (ADR-008).
  providersPageTitle: "Providers",
  providersPageDescription:
    "The outside services the platform sends email, signs people in and takes payments through.",
  emailSectionTitle: "Email",
  emailSectionDescription: "Every email the platform sends goes through the provider marked active.",
  signInSectionTitle: "Sign-in",
  signInSectionDescription: "Google and LinkedIn sign-in. Register each callback with the provider's app.",
  sendingCardTitle: "Sending",
  simulatorCardTitle: "Simulator (mailsim)",
  simulatorCardDescription: "Development and tests only. Production never sends through it.",
  sesCardTitle: "Amazon SES",
  sesCardDescription: "Production email, reached with the server's own AWS role. No key is stored anywhere.",
  resendCardTitle: "Resend",
  resendCardDescription:
    "The fallback when the primary cannot send for a while. Its API key is stored sealed.",
  providerFallback: "Fallback",
  providerResend: "Resend",
  providerNone: "No fallback",
  fieldFallbackProvider: "Fallback provider",
  fieldFallbackHint: "Takes over only when the primary cannot send right now, never for a refused message.",
  fieldResendApiKey: "API key",
  editResendTitle: "Edit Resend",
  editResendDescription: "Resend's API key. Takes effect on the next send, within seconds.",
  sendFallbackTestAction: "Test the fallback",
  swapAction: "Swap primary and fallback",
  swapTitle: "Swap primary and fallback",
  swapDescription:
    "The fallback starts sending every email and the primary becomes the fallback, within seconds.",
  swapSubmit: "Swap",
  toastTestEmailFellBack: "Sent through the fallback",
  resendHealthTitle: "Health",
  resendDomain: "Sending domain",
  resendProblemNoKey: "No API key is set.",
  resendProblemDenied: "Resend refused the API key.",
  resendProblemUnreachable: "Resend did not answer. Try again in a moment.",
  googleCardTitle: "Google",
  linkedinCardTitle: "LinkedIn",
  turnstileCardTitle: "Sign-up challenge (Cloudflare Turnstile)",
  turnstileCardDescription: "Asks a visitor's browser to prove it is not a script before sign-up.",
  turnstileFlagNote: "Also needs the sign-up-challenge flag on, under Flags.",
  fieldTurnstileSiteKey: "Site key",
  fieldTurnstileSecret: "Secret",
  editTurnstileTitle: "Edit the sign-up challenge",
  editTurnstileDescription:
    "Takes effect on the next sign-up, within seconds. A stored value replaces the environment's.",
  providerActive: "Active",
  providerInactive: "Inactive",
  providerConfigured: "Configured",
  providerIncomplete: "Incomplete",
  settingNotSet: "Not set",
  settingSet: "Set",
  settingFromEnvironment: "From",
  settingStoredBy: "Stored by",
  fieldProvider: "Active provider",
  fieldFromName: "From name",
  fieldFromAddress: "From address",
  fieldFromAddressHint: "On a domain SES has verified, before SES can be active.",
  fieldReplyTo: "Reply-to",
  fieldRegion: "Region",
  fieldConfigurationSet: "Configuration set",
  fieldEndpoint: "Endpoint",
  fieldSimulatorAddress: "Address",
  fieldIssuer: "Issuer",
  fieldClientId: "Client ID",
  fieldClientSecret: "Client secret",
  fieldClientSecretHint: "Type a new secret to replace it. It is never shown again.",
  fieldCallback: "Callback URL",
  fieldEmptyHint: "Empty it to go back to the environment's value.",
  endpointRegional: "The region's own AWS endpoint",
  providerSimulator: "Simulator",
  providerSes: "Amazon SES",
  editAction: "Edit",
  useEnvironment: "Use the environment's value",
  clearSecret: "Clear the stored secret",
  sendTestEmailAction: "Send a test email",
  checkAction: "Check",
  editSendingTitle: "Edit sending",
  editSendingDescription: "Which provider sends, and who the email says it is from.",
  editSesTitle: "Edit Amazon SES",
  editSesDescription: "The region and configuration set SES sends with. The server's role signs every call.",
  editSignInTitle: "Edit sign-in",
  editSignInDescription:
    "Takes effect on the next sign-in, within seconds. A stored value replaces the environment's.",
  saveSettings: "Save",
  noChanges: "Nothing changed yet.",
  sesHealthTitle: "Health",
  sesHealthLoading: "Asking SES",
  sesProblemDenied: "The server's role may not read SES. Check its IAM policy.",
  sesProblemUnreachable: "SES did not answer. Try again in a moment.",
  sesIdentity: "Sending domain",
  sesDkim: "DKIM",
  sesMailFrom: "MAIL FROM",
  sesAccess: "Account",
  sesSending: "Sending",
  sesQuota: "Sent in 24 hours",
  sesRate: "Send rate",
  sesConfigurationSetFound: "Configuration set",
  sesSuppressed: "Suppressed addresses",
  sesVerified: "Verified",
  sesNotVerified: "Not verified",
  sesPending: "Pending",
  sesFailed: "Failed",
  sesTemporaryFailure: "Temporary failure",
  sesNotStarted: "Not started",
  sesNotFound: "Not found",
  sesFound: "Found",
  sesProduction: "Production access",
  sesSandbox: "Sandbox",
  sesPaused: "Paused",
  toastSettingsSaved: "Settings saved",
  toastTestEmailSent: "Test email sent",
  toastCheckOk: "The issuer answered",
  toastCheckOkDescription: "Its discovery document is there and names this issuer.",
  toastCheckFailed: "The issuer did not answer as expected",
  checkUnreachable: "It could not be reached.",
  checkRefused: "It answered with an error.",
  checkNotOidc: "Its answer is not an OpenID Connect discovery document.",
  checkIssuerMismatch: "Its document names another issuer.",
  errorSettingsTitle: "Settings did not change",
  errorTestEmailTitle: "No test email was sent",
  errorCheckTitle: "The check did not run",

  // Website (S31.10): what the website's own build read at deploy time, read-only.
  websiteSectionTitle: "Website",
  websiteSectionDescription:
    "What the website's own build carries for analytics and search, read from its last deploy.",
  analyticsCardTitle: "Analytics (PostHog)",
  analyticsCardDescription: "The website's own PostHog project. Baked into its build, not stored here.",
  fieldPosthogKey: "Project key",
  fieldPosthogHost: "Region",
  fieldPosthogProjectId: "Project ID",
  openInPosthogAction: "Open in PostHog",
  websiteBuildTimeNote: "The website reads these at build time. Changing one needs a website deploy.",
  searchCardTitle: "Search (Google Search Console)",
  searchCardDescription: "The property Search Console watches for the website's own domain.",
  fieldSitemapUrl: "Sitemap",
  openSearchConsoleAction: "Open Search Console",
  searchHowToNote: "The domain is verified and the sitemap is submitted. Open Search Console to check it.",
} as const;

const ES: Translation<ProviderCatalog> = {
  // Providers (ADR-008).
  providersPageTitle: "Proveedores",
  providersPageDescription:
    "Los servicios externos con los que la plataforma envía correo, inicia sesión y cobra.",
  emailSectionTitle: "Correo",
  emailSectionDescription: "Cada correo que envía la plataforma sale por el proveedor marcado como activo.",
  signInSectionTitle: "Inicio de sesión",
  signInSectionDescription:
    "Inicio de sesión con Google y LinkedIn. Registra cada callback en la app del proveedor.",
  sendingCardTitle: "Envío",
  simulatorCardTitle: "Simulador (mailsim)",
  simulatorCardDescription: "Solo para desarrollo y pruebas. Producción nunca envía por aquí.",
  sesCardTitle: "Amazon SES",
  sesCardDescription:
    "Correo de producción, con el propio rol de AWS del servidor. No se guarda ninguna clave.",
  resendCardTitle: "Resend",
  resendCardDescription:
    "El respaldo cuando el principal no puede enviar por un rato. Su clave de API se guarda sellada.",
  providerFallback: "Respaldo",
  providerResend: "Resend",
  providerNone: "Sin respaldo",
  fieldFallbackProvider: "Proveedor de respaldo",
  fieldFallbackHint:
    "Entra solo cuando el principal no puede enviar en ese momento, nunca por un mensaje rechazado.",
  fieldResendApiKey: "Clave de API",
  editResendTitle: "Editar Resend",
  editResendDescription: "La clave de API de Resend. Se aplica en el próximo envío, en segundos.",
  sendFallbackTestAction: "Probar el respaldo",
  swapAction: "Intercambiar principal y respaldo",
  swapTitle: "Intercambiar principal y respaldo",
  swapDescription:
    "El respaldo empieza a enviar cada correo y el principal pasa a ser el respaldo, en segundos.",
  swapSubmit: "Intercambiar",
  toastTestEmailFellBack: "Enviado por el respaldo",
  resendHealthTitle: "Salud",
  resendDomain: "Dominio de envío",
  resendProblemNoKey: "No hay una clave de API.",
  resendProblemDenied: "Resend rechazó la clave de API.",
  resendProblemUnreachable: "Resend no respondió. Inténtalo de nuevo en un momento.",
  googleCardTitle: "Google",
  linkedinCardTitle: "LinkedIn",
  turnstileCardTitle: "Desafío de registro (Cloudflare Turnstile)",
  turnstileCardDescription: "Pide al navegador de quien se registra que demuestre que no es un script.",
  turnstileFlagNote: "También necesita el flag sign-up-challenge activo, en Flags.",
  fieldTurnstileSiteKey: "Clave del sitio",
  fieldTurnstileSecret: "Secreto",
  editTurnstileTitle: "Editar el desafío de registro",
  editTurnstileDescription:
    "Se aplica en el próximo registro, en segundos. Un valor guardado reemplaza al del entorno.",
  providerActive: "Activo",
  providerInactive: "Inactivo",
  providerConfigured: "Configurado",
  providerIncomplete: "Incompleto",
  settingNotSet: "Sin definir",
  settingSet: "Definido",
  settingFromEnvironment: "Desde",
  settingStoredBy: "Guardado por",
  fieldProvider: "Proveedor activo",
  fieldFromName: "Nombre del remitente",
  fieldFromAddress: "Dirección del remitente",
  fieldFromAddressHint: "En un dominio que SES haya verificado, antes de que SES pueda estar activo.",
  fieldReplyTo: "Responder a",
  fieldRegion: "Región",
  fieldConfigurationSet: "Conjunto de configuración",
  fieldEndpoint: "Endpoint",
  fieldSimulatorAddress: "Dirección",
  fieldIssuer: "Emisor",
  fieldClientId: "ID de cliente",
  fieldClientSecret: "Secreto de cliente",
  fieldClientSecretHint: "Escribe un secreto nuevo para reemplazarlo. No se vuelve a mostrar.",
  fieldCallback: "URL de callback",
  fieldEmptyHint: "Vacíalo para volver al valor del entorno.",
  endpointRegional: "El endpoint de AWS propio de la región",
  providerSimulator: "Simulador",
  providerSes: "Amazon SES",
  editAction: "Editar",
  useEnvironment: "Usar el valor del entorno",
  clearSecret: "Borrar el secreto guardado",
  sendTestEmailAction: "Enviar un correo de prueba",
  checkAction: "Comprobar",
  editSendingTitle: "Editar el envío",
  editSendingDescription: "Qué proveedor envía y de parte de quién dice ser el correo.",
  editSesTitle: "Editar Amazon SES",
  editSesDescription:
    "La región y el conjunto de configuración con los que envía SES. El rol del servidor firma cada llamada.",
  editSignInTitle: "Editar el inicio de sesión",
  editSignInDescription:
    "Se aplica en el próximo inicio de sesión, en segundos. Un valor guardado reemplaza al del entorno.",
  saveSettings: "Guardar",
  noChanges: "Aún no hay cambios.",
  sesHealthTitle: "Salud",
  sesHealthLoading: "Consultando a SES",
  sesProblemDenied: "El rol del servidor no puede leer SES. Revisa su política de IAM.",
  sesProblemUnreachable: "SES no respondió. Inténtalo de nuevo en un momento.",
  sesIdentity: "Dominio de envío",
  sesDkim: "DKIM",
  sesMailFrom: "MAIL FROM",
  sesAccess: "Cuenta",
  sesSending: "Envío",
  sesQuota: "Enviados en 24 horas",
  sesRate: "Velocidad de envío",
  sesConfigurationSetFound: "Conjunto de configuración",
  sesSuppressed: "Direcciones suprimidas",
  sesVerified: "Verificado",
  sesNotVerified: "Sin verificar",
  sesPending: "Pendiente",
  sesFailed: "Falló",
  sesTemporaryFailure: "Falla temporal",
  sesNotStarted: "Sin empezar",
  sesNotFound: "No encontrado",
  sesFound: "Encontrado",
  sesProduction: "Acceso de producción",
  sesSandbox: "Sandbox",
  sesPaused: "En pausa",
  toastSettingsSaved: "Ajustes guardados",
  toastTestEmailSent: "Correo de prueba enviado",
  toastCheckOk: "El emisor respondió",
  toastCheckOkDescription: "Su documento de descubrimiento está ahí y nombra a este emisor.",
  toastCheckFailed: "El emisor no respondió como se esperaba",
  checkUnreachable: "No se pudo conectar.",
  checkRefused: "Respondió con un error.",
  checkNotOidc: "Su respuesta no es un documento de descubrimiento de OpenID Connect.",
  checkIssuerMismatch: "Su documento nombra a otro emisor.",
  errorSettingsTitle: "Los ajustes no cambiaron",
  errorTestEmailTitle: "No se envió el correo de prueba",
  errorCheckTitle: "La comprobación no se ejecutó",

  // Website (S31.10): lo que la propia build del sitio leyó en su último despliegue, de solo lectura.
  websiteSectionTitle: "Sitio web",
  websiteSectionDescription:
    "Lo que la build del sitio lleva para analítica y búsqueda, leído de su último despliegue.",
  analyticsCardTitle: "Analítica (PostHog)",
  analyticsCardDescription: "El proyecto PostHog del sitio. Va incluido en su build, no se guarda aquí.",
  fieldPosthogKey: "Clave del proyecto",
  fieldPosthogHost: "Región",
  fieldPosthogProjectId: "ID del proyecto",
  openInPosthogAction: "Abrir en PostHog",
  websiteBuildTimeNote: "El sitio lee esto al compilar la build. Cambiarlo necesita un despliegue del sitio.",
  searchCardTitle: "Búsqueda (Google Search Console)",
  searchCardDescription: "La propiedad que Search Console vigila para el dominio del sitio.",
  fieldSitemapUrl: "Sitemap",
  openSearchConsoleAction: "Abrir Search Console",
  searchHowToNote:
    "El dominio está verificado y el sitemap está enviado. Abre Search Console para revisarlo.",
};

export const PROVIDER_CATALOGS: Catalogs<ProviderCatalog> = { en: EN, es: ES };
